import { c as _c } from "react/compiler-runtime";
/**
 * 반응형 환경에 따라 서로 다른 컴포넌트를 렌더링하는 헬퍼 컴포넌트입니다.
 *
 * 사용 예시
 * <Responsive desktop={<A />} mobile={<B />} />
 * <Responsive.DesktopOnly>데스크톱 전용</Responsive.DesktopOnly>
 * <Responsive.MobileOnly>모바일 전용</Responsive.MobileOnly>
 */
import { cn } from '@core/utils';
type ResponsiveDisplay = 'block' | 'inline-block' | 'inline-flex' | 'flex' | 'grid';
interface ResponsiveProps {
  desktop: React.ReactNode;
  mobile: React.ReactNode;
}
interface ResponsiveSubComponentProps {
  children: React.ReactNode;
  className?: string;
  display?: ResponsiveDisplay;
}
const DESKTOP_DISPLAY_CLASSES: Record<ResponsiveDisplay, string> = {
  block: 'block',
  flex: 'flex',
  'inline-block': 'inline-block',
  'inline-flex': 'inline-flex',
  grid: 'grid'
};
const MOBILE_DISPLAY_CLASSES: Record<ResponsiveDisplay, string> = {
  block: 'mobile-tablet:block',
  flex: 'mobile-tablet:flex',
  'inline-block': 'mobile-tablet:inline-block',
  'inline-flex': 'mobile-tablet:inline-flex',
  grid: 'mobile-tablet:grid'
};

/**
 * 데스크톱 해상도에서만 children을 렌더링합니다.
 */
const DesktopOnly = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    display: t1
  } = t0;
  const display = t1 === undefined ? "block" : t1;
  const t2 = DESKTOP_DISPLAY_CLASSES[display];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn(t2, "mobile-tablet:hidden", className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};

/**
 * 모바일/태블릿 해상도에서만 children을 렌더링합니다.
 */
const MobileOnly = t0 => {
  const $ = _c(6);
  const {
    children,
    className,
    display: t1
  } = t0;
  const display = t1 === undefined ? "block" : t1;
  const t2 = MOBILE_DISPLAY_CLASSES[display];
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("hidden", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const ResponsiveBase = t0 => {
  const $ = _c(7);
  const {
    desktop,
    mobile
  } = t0;
  let t1;
  if ($[0] !== desktop) {
    t1 = <DesktopOnly>{desktop}</DesktopOnly>;
    $[0] = desktop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== mobile) {
    t2 = <MobileOnly>{mobile}</MobileOnly>;
    $[2] = mobile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
type ResponsiveComponent = typeof ResponsiveBase & {
  DesktopOnly: typeof DesktopOnly;
  MobileOnly: typeof MobileOnly;
};
export const Responsive = Object.assign(ResponsiveBase, {
  DesktopOnly,
  MobileOnly
}) as ResponsiveComponent;